import { useStorefrontCategories } from "@/hooks/queries";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Skeleton } from "@/components/ui/skeleton";
import StandardImage from "@/components/shared/StandardImage";
import { useRef, useEffect, useState } from "react";

const CategoryBar = () => {
  const { data: categories = [], isLoading } = useStorefrontCategories();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Categories are already sorted by displayOrder and Name by the backend API
  const sortedCategories = categories;

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || sortedCategories.length === 0 || isHovered) return;

    const interval = setInterval(() => {
      const scrollStep = 180; // Amount to scroll in pixels (approx 2 small cards)
      const maxScrollLeft = container.scrollWidth - container.clientWidth;

      if (container.scrollLeft >= maxScrollLeft - 10) {
        // Return to start with smooth scrolling
        container.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        container.scrollTo({
          left: container.scrollLeft + scrollStep,
          behavior: "smooth",
        });
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [sortedCategories, isHovered]);

  return (
    <div className="relative w-full border-b border-border/40 bg-card/60 backdrop-blur-md z-20 shadow-sm py-2">
      {/* Edge gradient masks for horizontal scroll indication */}
      <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none z-10" />
      <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />

      <div className="container mx-auto px-4">
        <div
          ref={scrollContainerRef}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="flex items-center gap-4 sm:gap-6 overflow-x-auto scrollbar-none py-1.5 scroll-smooth"
        >
          {isLoading ? (
            Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="flex flex-col items-center gap-1.5 min-w-[90px] sm:min-w-[110px]">
                <Skeleton className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl" />
                <Skeleton className="h-3 w-16 rounded" />
              </div>
            ))
          ) : (
            sortedCategories.map((cat, i) => (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.015, duration: 0.2 }}
                className="flex-shrink-0"
              >
                <Link
                  to={`/products?category=${cat.id}`}
                  className="flex flex-col items-center gap-1.5 min-w-[90px] sm:min-w-[110px] group transition-transform duration-200 active:scale-95"
                >
                  <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl overflow-hidden bg-secondary/80 flex items-center justify-center p-2.5 border border-border/30 group-hover:border-primary/30 group-hover:bg-primary/5 transition-all duration-200 shadow-sm">
                    <StandardImage
                      src={cat.imageUrl}
                      alt={cat.name}
                      standard="category"
                      className="h-full w-full object-contain border-0 bg-transparent"
                      rounded="lg"
                      fallback={<span className="text-2xl sm:text-3xl font-bold text-primary">{cat.name[0]}</span>}
                    />
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-center text-foreground group-hover:text-primary transition-colors leading-tight truncate max-w-[84px] sm:max-w-[100px]">
                    {cat.name}
                  </span>
                </Link>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryBar;
